import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { MatDialog } from '@angular/material';
import { MatProgressButtonOptions } from 'mat-progress-buttons';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Observable, of, timer, zip } from 'rxjs';
import { finalize, map, switchMap } from 'rxjs/operators';
import { Assessment } from 'src/app/core/api/assessment.service';
import { StaffService } from 'src/app/core/api/staff.service';
import { AssessmentBody } from 'src/app/econsult/assessment-body/assessment-body.model';
import { EConsultService } from 'src/app/econsult/service/econsult.service';
import { SignsAttachmentsService } from 'src/app/encounters/signs/signs-attachments-modal/signs-attachments.service';
import { ConfirmModalService } from 'src/app/shared/confirm-modal/confirm-modal.service';
import {
  hasImpressionsSectionContent,
  upsertImpressionsSection
} from 'src/app/shared/impressions/impressions-sections.util';
import { InterpretationComponent } from 'src/app/shared/interpretation/interpretation.component';
import { SymptomModes } from 'src/app/shared/symptoms/symptom.model';
import { EConsultFormGroup } from '../../../../econsult/econsult-form.model';
import { FormErrorScrollingService } from '../../../form-error-scrolling/form-error-scrolling.service';
import { RiskFactorsModalComponent } from '../../../risk-factors-report/risk-factors-modal.component';
import { DryEyeMedicalHistoryFormGroup } from '../dry-eye-medical-history-form/dry-eye-medical-history-form.model';
import { PreOpService } from '../pre-op/pre-op.service';
import { TextFavoriteType } from './../../../../../API';
import { TextFavoriteService } from './../../../../logged-in-navbar/clinic-setup-modal/text-favorite/text-favorite.service';
import { DryEyeFormGroup } from './../dry-eye-form/dry-eye-form.model';
import { DryEyeExpansionPanel, DryEyeFormsService } from './../dry-eye-forms.service';
import { DryEyePredictionService } from './../dry-eye-prediction.service';
import { DryEyeAssessmentFormGroup } from './dry-eye-assessment-form.model';
import { TreatmentFiltersCDSService } from './treatment-filters/treatment-filters-cds.service';

@Component({
  selector: 'csi-dry-eye-assessment-form',
  templateUrl: './dry-eye-assessment-form.component.html',
  styleUrls: ['./dry-eye-assessment-form.component.scss'],
  animations: [
    trigger('predictionAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ])
  ]
})
export class DryEyeAssessmentFormComponent implements OnInit, OnDestroy {
  @Input() formGroup: DryEyeAssessmentFormGroup;
  @Input() medicalHistoryFormGroup: DryEyeMedicalHistoryFormGroup;
  @Input() showImageRequiredError = false;
  @Input() showCategorySelection = false;
  @Input() showImpressionsWarning$: Observable<boolean>;
  @Input() expandAttachments: boolean;
  @Input() symptomsAndSignsFormGroup: DryEyeFormGroup;
  @Input() saveFn: () => Observable<Assessment>;
  @Input() isLocked: boolean;

  public requiredErrors: string[];
  public normalizedErrors: string[];
  public readonly SymptomModes = SymptomModes;
  public readonly ExpansionPanel = DryEyeExpansionPanel;

  public readonly ML_VERSION = 3;

  public riskFactorTitle: string;

  public dryEyePrediction: string;

  public TextFavoriteType = TextFavoriteType;
  public waitingForPredict = false;

  public categoryAndSeverityMatSpinnerOptions: MatProgressButtonOptions = {
    text: 'Predict Dry Eye Type And Severity',
    active: false,
    raised: true,
    buttonColor: 'primary',
    spinnerSize: 19,
    mode: 'indeterminate'
  };

  public severityMatSpinnerOptions: MatProgressButtonOptions = {
    text: 'Predict Dry Eye Severity Risk',
    active: false,
    raised: true,
    buttonColor: 'primary',
    spinnerSize: 19,
    mode: 'indeterminate'
  };

  typing = false;
  typingDone = false;
  private typingDoneTimer?: any;
  private typeTimer?: any;
  private lastProgrammaticTarget: string | null = null;

  constructor(
    public filterService: TreatmentFiltersCDSService,
    private econsultFormGroup: EConsultFormGroup,
    private formErrorScrollingService: FormErrorScrollingService,
    private matDialog: MatDialog,
    private dryEyePredictionService: DryEyePredictionService,
    private changeDetector: ChangeDetectorRef,
    public staffService: StaffService,
    private textFavoriteService: TextFavoriteService,
    private confirmModalService: ConfirmModalService,
    public dryEyeFormsService: DryEyeFormsService,
    public preOpService: PreOpService,
    public signsAttachmentsService: SignsAttachmentsService,
    @Optional() private econsultService: EConsultService
  ) {}

  public HIDDEN_MODAL_CONFIG = {
    width: '0px',
    height: '0px',
    panelClass: 'hidden-modal',
    hasBackdrop: false
  };

  ngOnInit() {
    this.textFavoriteService.treatmentControlMap = {
      otcEyeDrops: this.formGroup.controls.recommendedEyeDrops,
      rxEyeDrops: this.formGroup.controls.recommendedRxEyeDrops,
      exercises: this.formGroup.controls.recommendedExercises,
      procedures: this.formGroup.controls.recommendedProcedures
    };
  }

  get severityClass(): 'mild' | 'moderate' | 'severe' | 'error' | '' {
    if (this.requiredErrors && this.requiredErrors.length) return 'error';
    if (!this.dryEyePrediction) return '';
    const severity = this.dryEyePrediction.toLowerCase();
    if (severity.startsWith('mild')) return 'mild';
    if (severity.startsWith('moderate')) return 'moderate';
    if (severity.startsWith('severe')) return 'severe';
    return '';
  }

  get hasImages(): boolean {
    return this.formGroup.controls.images.value && this.formGroup.controls.images.value.length > 0;
  }

  public showCDSModal() {
    this.matDialog.open(InterpretationComponent, {
      data: {
        patientData: this.flattenedCDSValues(),
        impressionsControl: this.formGroup.controls.impressions
      },
      minWidth: '60vw'
    });
  }

  public openRiskFactorsReportModal() {
    const isPreOpScreening =
      this.symptomsAndSignsFormGroup.controls.assessmentMethod.value ===
      SymptomModes.PreOpScreening;

    this.matDialog.open(RiskFactorsModalComponent, {
      data: {
        medicalHistoryData: this.medicalHistoryFormGroup.value,
        impressionsControl: this.formGroup.controls.impressions,
        isPreOpScreening
      }
    });
  }

  public scrollToFirstError() {
    this.econsultFormGroup.submitted = true;
    this.formErrorScrollingService.scrollToFirstInvalidControl();
  }

  // TODO: Only pass AssessmentBody to CDS and have it use methods to get the values it wants
  // See https://gitlab.com/clarity-lv/dry-eye-frontend/issues/197#note_207435011
  private flattenedCDSValues() {
    const data: AssessmentBody = this.econsultFormGroup.getRawValue() as AssessmentBody;

    const medicalHistoryFormValues = data.medicalHistoryForm;
    const dryEyeFormValues = data.dryEyeForm;

    if (!!dryEyeFormValues && !!medicalHistoryFormValues) {
      Object.keys(medicalHistoryFormValues).forEach(key => {
        if (!!dryEyeFormValues[key] && key !== 'schemaVersion') {
          console.error(
            'Warning: Found a duplicate key used in medicalHistoryForm and dryEyeForm called `' +
              key +
              '`. This may cause CDSComponent to display incorrect values.'
          );
        }
      });
    }

    return {
      ...medicalHistoryFormValues,
      ...dryEyeFormValues
    };
  }

  predictDryEyeCategoryAndSeverity() {
    this.requiredErrors = [];
    this.waitingForPredict = true;
    this.categoryAndSeverityMatSpinnerOptions.active = true;
    this.dryEyePredictionService
      .predictDryEyeCategoryAndSeverity()
      .pipe(
        switchMap(result => timer(2500).pipe(map(() => result))),
        finalize(() => {
          this.categoryAndSeverityMatSpinnerOptions.active = false;
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: (result: string) => {
          this.dryEyePrediction = result;
          this.waitingForPredict = false;
          this.requiredErrors = [];
          this.formGroup.controls.mlPrediction.setValue({
            ...(this.formGroup.controls.mlPrediction.value || {}),
            [this.ML_VERSION]: result
          });
          this.changeDetector.markForCheck();
        },
        error: (errors: string[]) => {
          this.requiredErrors = errors;
          this.waitingForPredict = false;
          this.changeDetector.markForCheck();
        }
      });
  }

  predictDryEyeSeverity() {
    this.severityMatSpinnerOptions.active = true;

    this.dryEyePredictionService
      .predictDryEyeSeverity()
      .pipe(
        finalize(() => (this.severityMatSpinnerOptions.active = false)),
        untilDestroyed(this)
      )
      .subscribe((result: string) => {
        this.dryEyePrediction = result;
        this.changeDetector.markForCheck();
      });
  }

  public getInterpretation(): Observable<string> {
    return this.matDialog
      .open(InterpretationComponent, {
        ...this.HIDDEN_MODAL_CONFIG,
        data: {
          patientData: this.flattenedCDSValues(),
          impressionsControl: this.formGroup.controls.impressions,
          closeAfterProcess: true
        }
      })
      .afterClosed();
  }

  public getRiskFactors(): Observable<string> {
    return this.matDialog
      .open(RiskFactorsModalComponent, {
        ...this.HIDDEN_MODAL_CONFIG,
        data: {
          medicalHistoryData: this.medicalHistoryFormGroup.value,
          impressionsControl: this.formGroup.controls.impressions,
          closeAfterProcess: true
        }
      })
      .afterClosed();
  }

  public analyzeAndCopyToImpression() {
    this.confirmImpressionsOverwrite().subscribe(confirmed => {
      if (!confirmed) return;
      this.runAnalyzeAndCopyToImpression();
    });
  }

  private runAnalyzeAndCopyToImpression() {
    this.predictDryEyeCategoryAndSeverity();
    this.waitingForPredict = true;

    const interpretation$ = this.getInterpretation();
    const riskFactors$ = this.getRiskFactors();
    zip(interpretation$, riskFactors$).subscribe(
      (contents: string[]) => {
        const [interpretationContent, riskFactorsContent] = contents;
        const currentImpressions = (this.formGroup.controls.impressions.value || '').toString();
        const withUpdatedInterpretation = upsertImpressionsSection(
          currentImpressions,
          'interpretation',
          (interpretationContent || '').toString()
        );
        const withUpdatedRiskFactors = upsertImpressionsSection(
          withUpdatedInterpretation,
          'riskFactors',
          (riskFactorsContent || '').toString()
        );
        this.beginImpressionsTyping(withUpdatedRiskFactors);
        setTimeout(() => (this.waitingForPredict = false), 2500);
      },
      error => {
        console.error('Error in combining data for impression:', error);
        setTimeout(() => (this.waitingForPredict = false), 2500);
      }
    );
  }

  private confirmImpressionsOverwrite(): Observable<boolean> {
    if (!this.hasAiSectionsToOverwrite()) {
      return of(true);
    }

    return this.confirmModalService.show(
      'impression.confirm.overwriteAiSectionsTitle',
      'impression.confirm.overwriteAiSectionsBody',
      'impression.confirm.overwriteSectionsButton',
      'Cancel'
    );
  }

  private hasAiSectionsToOverwrite(): boolean {
    const currentImpressions = (this.formGroup.controls.impressions.value || '').toString();
    return (
      hasImpressionsSectionContent(currentImpressions, 'interpretation') ||
      hasImpressionsSectionContent(currentImpressions, 'riskFactors')
    );
  }

  beginImpressionsTyping(text: string) {
    const ctrl = this.formGroup.controls.impressions;
    this.typeOutToControl(ctrl, text);
  }

  skipTyping() {
    if (!this.typing) return;
    const ctrl = this.formGroup.controls.impressions;
    const target =
      this.lastProgrammaticTarget !== null && this.lastProgrammaticTarget !== undefined
        ? this.lastProgrammaticTarget
        : '';
    this.finishTyping(ctrl, target);
  }

  private typeOutToControl(ctrl: any, target: string) {
    if (this.typeTimer) clearTimeout(this.typeTimer);
    this.typingDone = false;
    this.typing = true;
    this.changeDetector.markForCheck();
    this.lastProgrammaticTarget = target;

    const start = (ctrl.value || '').toString();
    const common = this.sharedPrefixLen(start, target);
    let i = Math.max(common, 0);

    ctrl.disable({ emitEvent: false });
    ctrl.setValue(target.slice(0, common), { emitEvent: false });

    const totalDurationMs = 2000;
    const startTime = performance.now();
    const totalDelta = target.length - common;

    const step = () => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / totalDurationMs, 1);

      i = Math.min(common + Math.round(totalDelta * progress), target.length);
      ctrl.setValue(target.slice(0, i), { emitEvent: false });

      if (progress >= 1) {
        return this.finishTyping(ctrl, target);
      }

      this.typeTimer = setTimeout(step, 16);
    };

    this.typeTimer = setTimeout(step, 16);
  }

  private finishTyping(ctrl: any, target: string) {
    if (this.typeTimer) clearTimeout(this.typeTimer);
    ctrl.setValue(target, { emitEvent: false });
    ctrl.enable({ emitEvent: false });
    this.typingDone = true;
    this.changeDetector.markForCheck();
    this.typing = false;
  }

  private sharedPrefixLen(a: string, b: string) {
    let i = 0;
    while (i < a.length && i < b.length && a[i] === b[i]) i++;
    return i;
  }

  ngOnDestroy() {}
}
